import { observer } from "mobx-react-lite";
import { Form as FinalForm, Field } from "react-final-form";
import { isRequired, combineValidators } from "revalidate";
import React, { useContext, useEffect } from "react";

// reactstrap components
import { Alert, Button, FormGroup, Form, Col, Row } from "reactstrap";
import { FORM_ERROR } from "final-form";
import TextInput from "../common/TextInput";
import { RootStoreContext } from "../../app/stores/rootStore";
import { IUserFormValues } from "../../app/models/user";

import { LogoCompany } from "../common/LogoCompany";
import { useTranslation } from "react-i18next";
import { translationHookOptions } from "../../app/localization/localization";

const validate = combineValidators({
  email: isRequired("email"),
  password: isRequired("password"),
});

export const LoginForm = () => {
  const rootStore = useContext(RootStoreContext);
  const { login } = rootStore.userStore;
  const { company, correctPassword} = rootStore.commonStore;
  const { switchLogin, showRegisterForm, showResetPwdForm } =
    rootStore.authStore;
  const [t] = useTranslation(["translation", "guest"], translationHookOptions);
  

  const changeLoginMethod = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    e.preventDefault();
    switchLogin();
  };

  const linkToRegister = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    e.preventDefault();
    showRegisterForm();
  };

  const linkToResetPwd = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    e.preventDefault();
    showResetPwdForm();
  };

  const handleMicrophoneEditRequest = (e : any) => {
    e.preventDefault();
    
    const userPassword = window.prompt(t("insert_password"));
    if (userPassword === correctPassword) {
      window.location.href = `${window.location.origin}/player//microphone`;
    } else {
      alert(t("incorrect_password"));
    }
  };

  return (
    <div className="form-wrapper mt-0">
      <div>
        <LogoCompany className="loginLogo" />
      </div>
      <h5>
        <b>{t("require_credentials", {ns:"translation"})}</b> 
      </h5>
      <FinalForm
        initialValues={{
          email: "",
          password: "",
          companyId: company ? company.id : 1,
        }}
        onSubmit={(values: IUserFormValues) =>
          login(values).catch((error) => ({
            [FORM_ERROR]: error,
          }))
        }
        validate={validate}
        render={({
          handleSubmit,
          submitting,
          submitError,
          invalid,
          pristine,
          dirtySinceLastSubmit,
        }) => (
          <Form onSubmit={handleSubmit} role="form">
            <FormGroup className="form-group input-group-lg">
              <Field
                className="form-control"
                name="email"
                component={TextInput}
                placeholder={t("email_placeholder")}
                autoComplete="email"
              />
            </FormGroup>
            <FormGroup className="form-group input-group-lg">
              <Field
                className="form-control"
                name="password"
                component={TextInput}
                placeholder={t("password_placeholder")}
                type="password"
                autoComplete="current-password"
              />
              <Field name="companyId" component={TextInput} type="hidden" />
            </FormGroup>
            {submitError && !dirtySinceLastSubmit && (
              <>
              {submitError.status === 400 ? (
                <Alert color="danger">{t("invalid_claims")}</Alert>
              ) : (
                <Alert color="danger">{t("invalid_credentials")}</Alert>
              )}
              </>
            )}
            {/* {submitError && !dirtySinceLastSubmit && (
              <Alert color="danger">{t("invalid_credentials")}</Alert>
            )} */}
            <div className="text-center">
              <Button
                disabled={(invalid && !dirtySinceLastSubmit) || pristine}
                className="btn loginButton btn-block btn-lg"
                color="dark"
                type="submit"
              >
                {t("login")}
              </Button>
            </div>
          </Form>
        )}
      />

      {company &&
        (company.alternativeLoginMethod === "otp" ||
          company.defaultLoginMethod === "otp") && (
          <>
            <div className="text-center text-muted mt-4 mb-2">
              <h6 className="linedText">
                <span className="linedSpan">{t("login_method_separator")}</span>
              </h6>
            </div>
            <div className="text-center text-muted mb-4">
              <Button
                className="loginOtpButton mt-2"
                onClick={(e: any) => changeLoginMethod(e)}
              >
                <b>{t("otp_alternative_login")}</b>
              </Button>
            </div>
          </>
        )}

      <Row className="mt-4">
        <Col style={{paddingRight:"0px"}}>
          <a
            className="text-muted text-underline"
            href="/#"
            onClick={(e) => linkToResetPwd(e)}
          >
            <u>{t("forgot_password")}</u>
          </a>
        </Col>
        {company && company.allowRegistration && (
          <Col>
            <a
              className="text-muted text-underline"
              href="/#"
              onClick={(e) => linkToRegister(e)}
            >
             {t("registration_text")}  {t("registration_link")}
            </a>
          </Col>
        )}
      </Row>

      {company && company.allowGuest && (
        <Row className="mt-2">
          <Col>
            <a className="text-muted text-underline" href="/guest">
              <u>{t("guest_login_link" , {ns:"guest"})}</u>
            </a>
          </Col>
        </Row>
      )}
      {company && company.allowBroadcast && (
        <Col className="text-center" xs="12" style={{marginTop: "10px"}}>
          <a className="text-center text-underline text-muted" href={`${window.location.origin}/player//microphone`} onClick={handleMicrophoneEditRequest}>
            <u>{t("microphone_link")}</u>
          </a>
        </Col>
      )}
    </div>
  );
};

export default observer(LoginForm);
